import { KindeUser } from "@inrange/building-manager-api-client/models-organisation";
import React from "react";
import { HeaderContainer, TabLink } from ".";
import EnergyActive from "../../images/icons/EnergyActive.svg";
import EnergyInActive from "../../images/icons/EnergyInActive.svg";
import PVSystemActive from "../../images/icons/PVSystemActive.svg";
import PVSystemInActive from "../../images/icons/PVSystemInActive.svg";
import SummaryActive from "../../images/icons/SummaryActive.svg";
import SummaryInActive from "../../images/icons/SummaryInActive.svg";
import PortfolioTab from "../PortfolioTab";
import SimpleButton from "../buttons/SimpleButton";
import { Row } from "../layout/Flex";
import ShareLinkWithUser from "../modal/ShareLinkWithUser";

interface EnergyOffer {
  label: string;
  acceptOfferFn: () => void;
  acceptOfferUrl: string;
}

interface SiteViewHeaderProps {
  siteId: string;
  currentUrl: string;
  rootPath: string;
  orgType: string;
  shareAttributes?: ShareAttributes;
  isOwnerOccupier: boolean;
  isOperational: boolean;
  energyOffer?: EnergyOffer;
  showPvSystemTab: boolean;
  showEnergySavingsTab: boolean;
  onDownloadCashflow?: () => void;
  downloadCashflowDisabled?: boolean;
}

export interface ShareAttributes {
  shareOrgUsers?: KindeUser[];
  currentOrgUsers: KindeUser[];
  userEmailsIsLoading: boolean;
  addUser: (
    addedUser: {
      firstName: string;
      lastName: string;
      email: string;
    },
    shareType: string
  ) => void;
  onShareButtonClick: (shareType: string) => void;
  shareOrgName?: string;
}

const SiteViewHeader: React.FC<SiteViewHeaderProps> = ({
  siteId,
  currentUrl,
  rootPath,
  orgType,
  shareAttributes,
  isOwnerOccupier,
  isOperational,
  energyOffer,
  showPvSystemTab,
  showEnergySavingsTab,
  onDownloadCashflow,
  downloadCashflowDisabled,
}) => {
  const summaryUrl = `${rootPath}site/${siteId}`;
  const energySavingsUrl = `${rootPath}site/${siteId}/energy-savings`;
  const pvSystemUrl = `${rootPath}site/${siteId}/pv-system`;
  const siteDocumentsUrl = `${rootPath}site/${siteId}/site-documents`;
  const billingsAndStatementsUrl = `${rootPath}site/${siteId}/billings-and-statements`;

  const savingsLabel =
    orgType === "Tenant" || isOwnerOccupier
      ? "Energy Procurement"
      : "Tenant Savings";
  const energyOfferWidth = energyOffer
    ? energyOffer.label.length > 20
      ? "180px"
      : "140px"
    : "0px";
  return (
    <HeaderContainer>
      <Row
        $padding={"0px"}
        $margin={"0px"}
        $alignItems={"center"}
        $justifyContent={"space-between"}
      >
        <TabLinkItem
          label="Site Summary"
          destinationUrl={summaryUrl}
          currentUrl={currentUrl}
          icon={currentUrl === summaryUrl ? SummaryActive : SummaryInActive}
          testid="site-summary-tab"
        />
        {showEnergySavingsTab && (
          <TabLinkItem
            label={savingsLabel}
            destinationUrl={energySavingsUrl}
            currentUrl={currentUrl}
            icon={
              currentUrl === energySavingsUrl ? EnergyActive : EnergyInActive
            }
            testid="energy-savings-tab"
          />
        )}
        {showPvSystemTab && (
          <TabLinkItem
            label="PV System"
            destinationUrl={pvSystemUrl}
            currentUrl={currentUrl}
            icon={
              currentUrl === pvSystemUrl ? PVSystemActive : PVSystemInActive
            }
            testid="pv-system-tab"
          />
        )}
        <TabLinkItem
          label="Site Documents"
          destinationUrl={siteDocumentsUrl}
          currentUrl={currentUrl}
          icon={
            currentUrl === siteDocumentsUrl ? SummaryActive : SummaryInActive
          }
          testid="site-documents-tab"
        />
        {isOperational && (
          <TabLinkItem
            label="Billing & Statements"
            destinationUrl={billingsAndStatementsUrl}
            currentUrl={currentUrl}
            icon={
              currentUrl === billingsAndStatementsUrl
                ? SummaryActive
                : SummaryInActive
            }
            testid="billings-and-statements-tab"
          />
        )}
        <Row
          $padding={"0px"}
          $margin={"0px 0px 0px auto"}
          $gap={"10px"}
          $alignItems={"center"}
          $justifyContent={"space-between"}
        >
          {onDownloadCashflow && (
            <SimpleButton
              fontWeight="400"
              height="40px"
              label={
                downloadCashflowDisabled
                  ? "Downloading..."
                  : "Download cash flow"
              }
              onClick={onDownloadCashflow}
              disabled={downloadCashflowDisabled}
            />
          )}
          {shareAttributes && (
            <ShareLinkWithUser
              button={isOwnerOccupier}
              orgType={orgType}
              shareAttributes={shareAttributes}
              energyOfferOffset={energyOfferWidth}
            />
          )}
          {energyOffer && (
            <SimpleButton
              fontWeight="400"
              height="40px"
              width={energyOfferWidth}
              label={energyOffer.label}
              to={energyOffer.acceptOfferUrl}
              onClick={energyOffer.acceptOfferFn}
            />
          )}
        </Row>
      </Row>
    </HeaderContainer>
  );
};

//work to be done on shades active icon and inactive icon
interface TabLinkItemProps {
  label: string;
  destinationUrl: string;
  currentUrl: string;
  icon: string;
  testid: string;
}

const TabLinkItem: React.FC<TabLinkItemProps> = ({
  label,
  destinationUrl,
  currentUrl,
  icon,
  testid,
}) => {
  return (
    <TabLink to={destinationUrl} data-testid={testid}>
      <PortfolioTab
        active={currentUrl === destinationUrl}
        tabName={label}
        icon={icon}
      />
    </TabLink>
  );
};

export default SiteViewHeader;
